import type {
  ICreateOrUpdateMwmAppChecklist,
  ICreateOrUpdatePublishingAppChecklist,
  ICreateOrUpdateReleaseChecklist,
  IChecklist,
} from '~/types/checklist'
import type { IAppVersion } from '~/types/version'

import config from '~/config'
import { request } from '~/utils/request'

export const createOrUpdateReleaseChecklist = ({
  changelog,
  developersTeamToAPI: developers_team,
  productManagerTeamToAPI: product_managers_team,
  productMonetizationSquadTeamToAPI: product_monetization_squad_team,
  relatedVersionToAPI: appVersionId,
  releaseDate: estimated_release_date,
}: ICreateOrUpdateReleaseChecklist): Promise<IAppVersion> => {
  return request({
    data: {
      assignation_mapping: {
        developers_team,
        product_managers_team,
        product_monetization_squad_team,
      },
      changelog: changelog,
      estimated_release_date: estimated_release_date,
    },
    method: 'PUT',
    url: `${config.VERSIONS_ADMIN}/application-version/${appVersionId}/release-checklist`,
  })
}

export const createPublishingAppChecklist = ({
  appId,
  backendTeamToAPI: backend_team,
  businessDevelopmentTeamToAPI: business_development,
  developersTeamToAPI: developers_team,
  productManagerTeamToAPI: product_managers_team,
  uaTeamToAPI: ua,
}: ICreateOrUpdatePublishingAppChecklist & { appId: string }): Promise<IAppVersion> => {
  return request({
    data: {
      assignation_mapping: {
        backend_team,
        business_development,
        developers_team,
        product_managers_team,
        ua,
      },
    },
    method: 'POST',
    url: `${config.VERSIONS_ADMIN}/application/${appId}/new-pp-app`,
  })
}

export const updatePublishingAppChecklist = ({
  appId,
  backendTeamToAPI: backend_team,
  checklistId,
  developersTeamToAPI: developers_team,
  productManagerTeamToAPI: product_managers_team,
  uaTeamToAPI: ua,
}: ICreateOrUpdatePublishingAppChecklist & { appId: string; checklistId: string }): Promise<IAppVersion> => {
  return request({
    data: {
      assignation_mapping: {
        backend_team,
        developers_team,
        product_managers_team,
        ua,
      },
    },
    method: 'PUT',
    url: `${config.VERSIONS_ADMIN}/application/${appId}/new-pp-app/${checklistId}`,
  })
}

export const createOrUpdateMwmAppChecklist = ({
  adminTeamToAPI: admin,
  appId,
  backendTeamToAPI: backend_team,
  checklistId,
  dataTeamToAPI: data_team,
  developersTeamToAPI: developers_team,
  marketingTeamToAPI: marketing_team,
  monetizationTeamToAPI: monetization_team,
  productOwnerTeamToAPI: product_owners,
  uaTeamToAPI: ua,
}: ICreateOrUpdateMwmAppChecklist & { appId: string; checklistId?: string }): Promise<IAppVersion> => {
  return request({
    data: {
      assignation_mapping: {
        admin,
        backend_team,
        data_team,
        developers_team,
        marketing_team,
        monetization_team,
        product_owners,
        ua,
      },
    },
    method: checklistId ? 'PUT' : 'POST',
    url: `${config.VERSIONS_ADMIN}/application/${appId}/new-application-checklist${
      checklistId ? `/${checklistId}` : ''
    }`,
  })
}

type QueryParams = {
  application_id: string
  application_version_id?: string
  page?: number
  size?: number
}

export const getChecklists = (queryParams: QueryParams): Promise<IPaginatedResponse<IChecklist>> => {
  const params = (Object.keys(queryParams) as (keyof QueryParams)[]).map((key) => `${key}=${queryParams[key]}`)
  const endpointSuffix = params.length > 0 ? `?${params.join('&')}` : ''

  return request({
    url: `${config.CHECKLIST_API}/checklist${endpointSuffix}`,
  })
}

export const getChecklist = (checklistId: string): Promise<IChecklist> => {
  return request({
    url: `${config.CHECKLIST_API}/checklist/${checklistId}`,
  })
}

export const notifyStatusChange = ({
  checklistId,
  status,
  taskId,
}: {
  checklistId: string
  status: 'done' | 'skipped'
  taskId: string
}): Promise<void> => {
  return request({
    method: 'POST',
    url: `${config.CHECKLIST_API}/checklist/${checklistId}/task/${taskId}/validate/${status}`,
  })
}
